import { defineRecipe } from '@chakra-ui/react';

/*
 * v3 port notes: the built-in `heading` recipe's `size` variant sets
 * `textStyle` (a composite token expanding to fontSize/lineHeight/etc. from
 * `theme.ts`'s own `textStyles`, unrelated to this app's design). Confirmed
 * via `createSystem`/`getRecipeFn` (same empirical technique used
 * throughout this migration) that explicit literal `fontSize`/`lineHeight`
 * below fully wins over the built-in's `textStyle`, with zero bleed-through
 * in the compiled output — no extra neutralization needed, same as how
 * Tag.ts's `base.label` restate already handles the identical shape.
 *
 * Responsive sizes step CSS vars rather than `fontSize`/`lineHeight`. v3
 * merges recipe and prop styles per breakpoint (v2 replaced the whole
 * value), so a responsive recipe `fontSize` keeps its `md` step under a
 * call-site `fontSize="md"` — the default `xl` size rendered 30px instead of
 * 14px from 701px up. Pointing the properties at vars lets a call-site
 * `fontSize`/`lineHeight` replace every step, as in v2.
 */

const responsiveSize = (
  [baseFontSize, mdFontSize]: [string, string],
  [baseLineHeight, mdLineHeight]: [number, number],
) => ({
  '--heading-font-size': {
    base: `fontSizes.${baseFontSize}`,
    md: `fontSizes.${mdFontSize}`,
  },
  '--heading-line-height': { base: baseLineHeight, md: mdLineHeight },
  fontSize: 'var(--heading-font-size)',
  lineHeight: 'var(--heading-line-height)',
});

export const headingTheme = defineRecipe({
  base: {
    fontFamily: 'heading',
    fontWeight: 600,
    color: 'fg.strong',
    letterSpacing: 'tight', // -0.02em per design system
  },
  variants: {
    size: {
      '4xl': responsiveSize(['6xl', '7xl'], [1, 1]),
      '3xl': responsiveSize(['5xl', '6xl'], [1, 1]),
      '2xl': responsiveSize(['4xl', '5xl'], [1.2, 1]),
      xl: responsiveSize(['3xl', '4xl'], [1.33, 1.2]),
      lg: responsiveSize(['2xl', '3xl'], [1.33, 1.2]),
      md: { fontSize: 'xl', lineHeight: 1.2 },
      sm: { fontSize: 'sm', lineHeight: 1.2 },
      xs: { fontSize: 'sm', lineHeight: 1.2 },
    },
  },
  defaultVariants: {
    size: 'xl',
  },
});

export default headingTheme;
